import { useState } from 'react';
import {
  Trash2,
  Loader2,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useOffline } from '@/hooks/useOffline';

export default function SellStatus() {
  const { resetLocalData, pendingCount } = useOffline();
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleResetData = async () => {
    setIsResetting(true);
    try {
      await resetLocalData();
      setShowResetConfirm(false);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <DashboardLayout
      header={
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Status
        </h1>
      }
    >
      <div className="max-w-2xl space-y-6">
        <div>
          <h3 className="text-base font-semibold">Reset local data</h3>
          <p className="text-sm text-muted-foreground mt-1">
            We keep a copy of some of your store data in your web browser so you can still sell if
            you lose internet connection. Sometimes, this gets out of sync. Resetting it can help if
            you're having trouble with BlazePOS.
          </p>
        </div>

        <Button
          variant="destructive"
          onClick={() => setShowResetConfirm(true)}
          disabled={isResetting}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Reset data
        </Button>
      </div>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Local Data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear all cached data stored in your browser and re-download fresh data from
              the server. Any unsynced changes will be lost.
              {pendingCount > 0 && (
                <span className="block mt-2 font-medium text-destructive">
                  Warning: You have {pendingCount} pending item(s) that have not been synced.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetData}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset data'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
